import { useMutation } from '@apollo/client';
import {
  ACCEPT_COMPANY_INVITE,
  Button,
  ErrorMessage,
  useAuthUser,
} from '@employ-engage/common-components';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { AuthStatusEnum } from '@employ-engage/data';

/**
 * Where an invitation link lands.
 *
 * The token is the authorization, not the address it was sent to, so someone
 * may accept as whichever identity they actually use. That means this page has
 * two jobs: redeem the token for a visitor who is already signed in, and let
 * one who is not create an account that goes straight into the inviting
 * company.
 *
 * The second is why the token is passed to signup as a form field rather than
 * redeemed afterwards: the server joins the new account to the company inside
 * the same call that creates it, so there is never a moment where a user
 * exists with nowhere to be.
 *
 * The token is never stored. It stays in the URL for the length of one visit.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation(['common']);
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { user, status, signupWithInvite } = useAuthUser();

  const [acceptInvite] = useMutation(ACCEPT_COMPANY_INVITE);
  const [state, setState] = useState<'working' | 'signup' | 'done' | 'error'>(
    'working'
  );
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState<{
    name?: string;
    friendlyCompanyId?: string;
  } | null>(null);

  // React mounts effects twice in development, and accepting is single-use:
  // the second call reports an invitation that is no longer open.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage(t('This invitation link is missing its token.'));
      return;
    }
    // Wait for the session check to settle before deciding which job this is.
    if (status === AuthStatusEnum.Pending) return;
    if (!user) {
      setState('signup');
      return;
    }
    if (attempted.current) return;
    attempted.current = true;

    acceptInvite({ variables: { token } })
      .then(({ data }) => {
        setCompany(data?.acceptCompanyInvite ?? null);
        setState('done');
      })
      .catch((error) => {
        setState('error');
        // The server's messages are written to be read — "expired or no longer
        // valid" — so they are shown rather than replaced with something vaguer.
        setMessage(error?.message ?? t('This invitation could not be accepted.'));
      });
  }, [acceptInvite, token, t, user, status]);

  const form = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('Required')).required(t('Required')),
      password: Yup.string().min(8, t('At least 8 characters')).required(t('Required')),
    }),
    onSubmit: async ({ email, password }) => {
      try {
        await signupWithInvite(email, password, token);
        // The account is already in the company; reload so every context
        // picks up the new session rather than reasoning about it here.
        window.location.assign('/');
      } catch (error: any) {
        setMessage(error?.message ?? t('Could not create your account.'));
      }
    },
  });

  const goHome = (
    <div className="mt-6">
      <Button variant="secondary" to="/">
        {t('Go to sign in')}
      </Button>
    </div>
  );

  return (
    <div className="mx-auto mt-20 max-w-lg rounded-lg bg-white p-8 shadow">
      {state === 'working' && (
        <p className="text-center">{t('Accepting your invitation…')}</p>
      )}

      {state === 'signup' && (
        <form onSubmit={form.handleSubmit}>
          <h1 className="text-center text-xl font-semibold">
            {t('Create your account')}
          </h1>
          <p className="mt-2 text-center text-sm text-light-secondaryText">
            {t(
              'You can use any email address — the invitation is what lets you in.'
            )}
          </p>
          <div className="mt-6 space-y-3">
            <input
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t('Email')}
              className="w-full rounded-md border-2 border-gray-400 p-2"
              value={form.values.email}
              onChange={form.handleChange}
            />
            <ErrorMessage
              errors={form.errors.email}
              touched={form.touched.email}
            />
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder={t('Password')}
              className="w-full rounded-md border-2 border-gray-400 p-2"
              value={form.values.password}
              onChange={form.handleChange}
            />
            <ErrorMessage
              errors={form.errors.password}
              touched={form.touched.password}
            />
          </div>
          {!!message && (
            <p className="mt-3 text-sm text-light-error" role="alert">
              {message}
            </p>
          )}
          <div className="mt-6">
            <Button type="submit" variant="primary" fullWidth>
              {t('Create account and join')}
            </Button>
          </div>
          <p className="mt-4 text-center text-sm text-light-secondaryText">
            {t('Already have an account?')}{' '}
            <a className="underline" href="/">
              {t('Sign in')}
            </a>{' '}
            {t('and open this link again.')}
          </p>
        </form>
      )}

      {state === 'done' && (
        <div className="text-center">
          <h1 className="text-xl font-semibold">
            {company?.name
              ? t('You have joined {{company}}', { company: company.name })
              : t('Invitation accepted')}
          </h1>
          <div className="mt-6">
            <Button
              variant="primary"
              to={
                company?.friendlyCompanyId
                  ? `/${company.friendlyCompanyId}/admin/dashboard`
                  : '/'
              }
            >
              {t('Continue')}
            </Button>
          </div>
        </div>
      )}

      {state === 'error' && (
        <div className="text-center">
          <h1 className="text-xl font-semibold">
            {t('This invitation could not be accepted')}
          </h1>
          <p className="mt-2 text-sm text-light-secondaryText">{message}</p>
          {goHome}
        </div>
      )}
    </div>
  );
}

export default AcceptInvitePage;
